import { createContext, useContext } from 'react';

import type { AssetRepository } from '@/features/assets/assetRepository';
import type { CategoryRepository } from '@/features/categories/categoryRepository';

/**
 * The set of data-layer repositories the rest of the app depends on. The live
 * Firestore implementations are wired in at boot via `<RepositoryProvider>`
 * (see `app/providers.tsx`); tests inject in-memory fakes via the same context.
 */
export interface Repositories {
  assets: AssetRepository;
  categories: CategoryRepository;
}

const RepositoryContext = createContext<Repositories | null>(null);

/** Injects repository implementations into the component tree. */
export const RepositoryProvider = RepositoryContext.Provider;

/**
 * Pull the active repository bundle out of context. Throws when no provider
 * is in scope so misuse fails loudly during development.
 */
export const useRepositories = (): Repositories => {
  const ctx = useContext(RepositoryContext);
  if (!ctx) throw new Error('useRepositories must be used inside <RepositoryProvider>');
  return ctx;
};
